import { useState } from "react";
import { riseIn } from "../../style/styled-components/keyframes/fade";
import { menucontext } from "../../context/nav/menucontext";
import UseBreakpoints from "../hooks/ui/UseBreakpoints";
import { languageStore } from "../../state/language";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";

// 최상위 메뉴를 누르면 첫 하위 페이지로 이동
const firstUrl = (x) =>
  x.url?.startsWith("/") ? x.url : x.children?.map(firstUrl).find(Boolean);
// 현재 경로가 이 메뉴(하위 포함)에 속하는지
const contains = (x, path) =>
  (x.url?.startsWith("/") && (path === x.url || path.startsWith(x.url + "/"))) ||
  !!x.children?.some((c) => contains(c, path));

const Nav = () => {
  const breakpoints = UseBreakpoints();
  const { language } = languageStore();
  const { pathname } = useLocation();
  const [open, setOpen] = useState("");
  const t = (x) => (language === "EN" && x.engtitle ? x.engtitle : x.title);
  if (!breakpoints.xl) return <Spacer />;
  const current = menucontext.children.find((x) => x.key === open);
  const close = () => setOpen("");
  return (
    <Div onMouseLeave={close}>
      <ul>
        {menucontext.children.filter((x) => x).map((x) => (
          <li key={x.key} onMouseEnter={() => setOpen(x.key)}>
            <TopLink
              to={firstUrl(x)}
              draggable={false}
              $on={open === x.key || (!open && contains(x, pathname))}
              onFocus={() => setOpen(x.key)}
              onClick={close}
            >
              {t(x)}
            </TopLink>
          </li>
        ))}
      </ul>
      {current?.children && (
        <Panel key={current.key}>
          <div className="inner">
            <div className="head">
              {language !== "EN" && current.engtitle && <span>{current.engtitle}</span>}
              <strong>{t(current)}</strong>
            </div>
            <div className={current.children.some((c) => c.children) ? "groups" : "links"}>
              {current.children.map((c) =>
                c.children ? (
                  <div key={c.key} className="group">
                    <span>{t(c)}</span>
                    {c.children.map((z) => (
                      <Link key={z.key} to={z.url} draggable={false} onClick={close}>
                        {t(z)}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link key={c.key} to={c.url} draggable={false} onClick={close}>
                    {t(c)}
                    <i aria-hidden="true">→</i>
                  </Link>
                )
              )}
            </div>
          </div>
        </Panel>
      )}
    </Div>
  );
};
export default Nav;
const Spacer = styled.div`
  flex: 1;
`;
const Div = styled.nav`
  flex: 1;
  height: 100%;
  display: flex;
  justify-content: center;
  & > ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    align-items: stretch;
    gap: clamp(1.5rem, 2.6vw, 3rem);
    height: 100%;
    & > li {
      display: flex;
    }
  }
`;
const TopLink = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  font-size: 1.0625rem;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $on, theme }) => ($on ? theme.colors.accent : theme.colors.foreground.normal)};
  transition: color 0.2s;
  &:after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 3px;
    border-radius: 3px 3px 0 0;
    background-color: ${({ theme }) => theme.colors.accent};
    transform: scaleX(${({ $on }) => ($on ? 1 : 0)});
    transition: transform 0.25s ease;
  }
`;
const Panel = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  box-shadow: 0 24px 48px -12px rgba(11, 22, 61, 0.18);
  animation: ${riseIn} 0.22s ease-out;
  & > .inner {
    max-width: 90rem;
    margin: 0 auto;
    padding: 2.5rem clamp(1rem, 3vw, 2.5rem) 3rem;
    box-sizing: border-box;
    display: flex;
    gap: 3rem;
  }
  & .head {
    flex: 0 0 15rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding-right: 2rem;
    border-right: 1px solid ${({ theme }) => theme.colors.line};
    & > span {
      font-size: 0.8rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${({ theme }) => theme.colors.accent};
    }
    & > strong {
      font-size: 1.75rem;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.dark};
    }
  }
  & .groups {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 1.5rem;
    & > .group {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      & > span {
        font-size: 0.85rem;
        font-weight: 700;
        color: ${({ theme }) => theme.colors.dark};
        padding-bottom: 0.6rem;
        margin-bottom: 0.4rem;
        border-bottom: 2px solid ${({ theme }) => theme.colors.dark};
      }
      & > a {
        padding: 0.4rem 0;
        color: ${({ theme }) => theme.colors.foreground.dimmed};
        transition: color 0.15s;
        &:hover {
          color: ${({ theme }) => theme.colors.accent};
        }
      }
    }
  }
  & .links {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
    align-content: start;
    & > a {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem 1.25rem;
      border-radius: 0.75rem;
      background-color: ${({ theme }) => theme.colors.monotone.extralight};
      font-weight: 600;
      color: ${({ theme }) => theme.colors.foreground.normal};
      transition: background-color 0.15s, color 0.15s;
      & > i {
        font-style: normal;
        color: ${({ theme }) => theme.colors.monotone.medium};
        transition: transform 0.15s;
      }
      &:hover {
        background-color: ${({ theme }) => theme.colors.accentSoft};
        color: ${({ theme }) => theme.colors.accent};
        & > i {
          color: ${({ theme }) => theme.colors.accent};
          transform: translateX(3px);
        }
      }
    }
  }
`;
